import { createContext, useContext, useId, type LabelHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';

interface FieldContextValue {
  controlId: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/**
 * Read by Input / Select / Textarea so that a control dropped inside a <Field> is wired
 * to its label, help text and error message without the caller repeating ids
 * (rules §48.3, §17.5). Returns null outside a Field, where the caller owns the wiring.
 */
export function useFieldControl(): FieldContextValue | null {
  return useContext(FieldContext);
}

export interface FieldProps {
  label: ReactNode;
  /** Persistent guidance shown under the control. */
  help?: ReactNode;
  /** Validation message. Its presence puts the control into the invalid state. */
  error?: ReactNode;
  required?: boolean;
  /** Override the generated id, e.g. to deep-link to a field from a summary of errors. */
  id?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Label + control + help + error, in the funnel's rhythm: label 13/500/neutral-700 with
 * a 5px gap to the control (tokens doc §7.3).
 */
export function Field({
  label,
  help,
  error,
  required = false,
  id,
  className,
  children,
}: FieldProps) {
  const generated = useId();
  const controlId = id ?? generated;
  const helpId = `${controlId}-help`;
  const errorId = `${controlId}-error`;

  const describedBy =
    [help === undefined || help === null ? null : helpId, error ? errorId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <FieldContext.Provider value={{ controlId, describedBy, invalid: Boolean(error), required }}>
      <div className={cn('flex flex-col', className)}>
        <Label htmlFor={controlId} required={required}>
          {label}
        </Label>
        {children}
        {help === undefined || help === null ? null : <HelpText id={helpId}>{help}</HelpText>}
        {error ? <FieldError id={errorId}>{error}</FieldError> : null}
      </div>
    </FieldContext.Provider>
  );
}

export interface LabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
  children: ReactNode;
}

/** 13px / 500 / neutral-700, 5px above the control. */
export function Label({ required = false, className, children, ...rest }: LabelProps) {
  return (
    <label className={cn('mb-[5px] text-sm font-medium text-neutral-700', className)} {...rest}>
      {children}
      {required ? (
        <>
          <span aria-hidden="true" className="ml-[2px] text-danger">
            *
          </span>
          <span className="sr-only"> (required)</span>
        </>
      ) : null}
    </label>
  );
}

export interface HelpTextProps {
  id?: string;
  className?: string;
  children: ReactNode;
}

/** The `.secure-note` scale: 12px / neutral-400. */
export function HelpText({ id, className, children }: HelpTextProps) {
  return (
    <p id={id} className={cn('mt-[5px] text-xs text-neutral-400-text', className)}>
      {children}
    </p>
  );
}

export interface FieldErrorProps {
  id?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Error text carries an icon glyph as well as the color, so state is never communicated
 * by color alone (rules §48.5). `role="alert"` announces it when it appears.
 */
export function FieldError({ id, className, children }: FieldErrorProps) {
  return (
    <p
      id={id}
      role="alert"
      className={cn('mt-[5px] flex items-start gap-1 text-sm text-danger-ink', className)}
    >
      <span aria-hidden="true" className="leading-5">
        &#9888;
      </span>
      <span>{children}</span>
    </p>
  );
}
